import axios from "axios";
import { isEmpty, omitBy, isNil, isNull } from "lodash";
import normalize from "Utils/normalizer";
import useSession from "Hooks/session.hook";
import errorHandler from "Utils/error-handler";
import { getAccessToken } from "Helpers/session";
import { apiURL } from "Helpers/index";
import moment, { Moment } from "moment";
import { SameOrderModel } from "Data/order";

const url = `${process.env.API_URL}/${process.env.API_VER}`;

interface Pagination {
  offset: number;
  limit: number;
}

interface Sorting {
  sortBy: string;
  mode: "ASC" | "DESC";
}

interface Filter {
  _id?: string;
  orgID?: string;
  orgIDs?: Array<any>;
  companyOrgID?: string;
  orderStatus?: string;
  orFields?: string;
}

export const createOrder = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/create`,
      data: inputData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createOrder(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const calculateOrder = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  // return { success: true }
  try {
    const response: any = await axios({
      method: "POST",
      url: `${url}/order/calculate_order_charges/fetch`,
      data: normalize.model.order(inputData),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.error) return response;

    // check for two kinds of orders
    if (SameOrderModel.includes(inputData?.orderType?.toUpperCase()))
      return { order: response.data };
    else return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await calculateOrder(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const calculateUpdatedOrder = async (inputData: any) => {
  const accessToken = getAccessToken();

  try {
    const response: any = await axios({
      method: "POST",
      url: `${url}/order/calculate_order_charges_update/fetch`,
      data: normalize.model.order(inputData),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.error) return response;

    // check for two kinds of orders
    if (SameOrderModel.includes(inputData?.orderType?.toUpperCase()))
      return { order: response.data };
    else return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await calculateUpdatedOrder(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateOrder = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  // return { success: true }
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/update`,
      data: normalize.model.order(inputData),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateOrder(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchOrderList = async (
  // filters: Filters,
  filters: Filter,
  // orgID?: string,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const url = apiURL("/order/list");
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: omitBy({ filters: filters, pagination, sorting }, isNil),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !isEmpty(response.data.orders)
      ? normalize.response.orderList(response.data.orders)
      : [];
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchOrderList(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      return { error: { message: err.message } };
    }
  }
};

export const fetchDeliveryAndServiceTypeList = async (
  customerOrgID?: String,
  postcode?: string,
) => {
  const url = apiURL("/order_processing_setting/postcode/services/fetch");
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: omitBy({ customerOrgID, postcode }, isNil),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response?.data.options;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchDeliveryAndServiceTypeList(postcode);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchGoodsCareConditionList = async (
  customerOrgID?: String,
  orderSettingTypes?: Array<any>,
) => {
  const url = apiURL("/order_processing_setting/types/fetch");
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: omitBy({ customerOrgID, orderSettingTypes }, isNil),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response?.data.options;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchGoodsCareConditionList(customerOrgID, orderSettingTypes);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const handleBulkOrderValidation = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/bulk/list`,
      data: omitBy(inputData, isNil),
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);
    return { error: { message: err.message } };
  }
};

export const handleBulkOrderCreate = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();
  const payload = { orders: inputData.orders };
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/bulk/create`,
      data: omitBy(payload, isNil),
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);
    return {
      error: {
        message: error.message,
        code: error.code,
        stack: error.stack,
        info: error?.info ?? null,
      },
    };
  }
};

export const updateOrderStatus = async (
  orderID: string[],
  newStatus: string,
  issueType,
  remarks?: string,
) => {
  const accessToken = getAccessToken();
  const payload = omitBy(
    {
      orderID: orderID,
      newStatus: newStatus,
      issueType: issueType,
      remarks: remarks,
    },
    isNull,
  );
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/status/update`,
      data: { ...payload },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await updateOrderStatus(orderID, newStatus, issueType, remarks);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const cancelRejectOrder = async (
  orderID: string,
  newStatus: string,
  remarks?: string,
  issueType?: any,
) => {
  const accessToken = getAccessToken();
  const payload = omitBy(
    {
      _id: orderID,
      newStatus,
      issueType: issueType ? [{
        orderSettingID: issueType.id,
        remarks: issueType.remarks,
        name: issueType.name
      }] : null,
      remarks,
    },
    isNull,
  );

  try {
    const response = await axios({
      method: "POST",
      url: apiURL("/order/cancel_or_reject/update"),
      data: { orders: [payload] },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await cancelRejectOrder(orderID, newStatus, remarks);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateReverseOrderStatus = async (
  orderID: string[],
  newStatus: string,
  issueType,
  remarks?: string,
) => {
  const accessToken = getAccessToken();
  const payload = omitBy(
    {
      orderID: orderID,
      newStatus: newStatus,
      issueType: issueType,
      remarks: remarks,
    },
    isNull,
  );
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/reverse_status/update`,
      data: { ...payload },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await updateReverseOrderStatus(orderID, newStatus, issueType, remarks);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createOrderNotes = async (inputData: any, orderID) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  // return { success: true }
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/notes/create`,
      data: {
        orderID: orderID,
        notes: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.order(response?.data?.note?.new);
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createOrder(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createOrderCharges = async (inputData: any, orderID) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  // return { success: true }
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/add_charges/update`,
      data: {
        orderID: orderID,
        ...inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.order(response?.data?.charges?.new);
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createOrderCharges(inputData, orderID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const deleteOrderCharges = async (inputData: any, orderID) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  // return { success: true }
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/delete_charges/update`,
      data: {
        orderID: orderID,
        chargeName: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.order(response?.data?.charges?.new);
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await deleteOrderCharges(inputData, orderID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateOrderNotes = async (inputData: any, orderID) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  // return { success: true }
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/notes/update`,
      data: {
        orderID: orderID,
        notes: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.order(response?.data?.note?.new);
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateOrderNotes(inputData, orderID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchOrder = async (orderID: string, isEdit: boolean = false) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/fetch`,
      data: {
        orderID: orderID,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return normalize.response.order(response.data, isEdit);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchOrder(orderID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchOrderStatisticsCount = async (orgIDs: string[]) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/statistics/fetch`,
      data: { orgID: orgIDs },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchOrderStatisticsCount(orgIDs);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const bulkLooseAssignDriver = async (orderDriverInput) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/assign_driver/update`,
      data: { ...orderDriverInput },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await bulkLooseAssignDriver(orderDriverInput);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateReturnOrder = async (data: any) => {
  const accessToken = getAccessToken();
  const payload = omitBy(data, isNull);
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/return_info/update`,
      data: { ...payload },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateReturnOrder(data);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ARCHIVE SETTINGS

export const createArchiveSetting = async (archiveSettingInput: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/archive_setting/create`,
      data: { archiveSetting: archiveSettingInput },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await createArchiveSetting(archiveSettingInput);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchArchiveSettings = async (orgID, type = "ORDER") => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/archive_setting/fetch`,
      data: { orgID: orgID, type: type },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchArchiveSettings(orgID, (type = "ORDER"));
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateArchiveSettings = async (data) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/archive_setting/update`,
      data: { ...data },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateArchiveSettings(data);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ARCHIVE ORDERS

export const listArchiveOrders = async (
  filters?: Filter,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order_archive/list`,
      data: omitBy({ filters: filters, pagination, sorting }, isNil),
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listArchiveOrders(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchArchiveOrders = async (orderArchiveID) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order_archive/fetch`,
      data: { orderArchiveID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return normalize.response.order(response.data);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchArchiveOrders(orderArchiveID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createArchiveOrders = async (orderIDs) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order_archive/create`,
      data: { orderIDs },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createArchiveOrders(orderIDs);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const revertArchiveOrders = async (orderArchiveIDs) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order_archive/revert`,
      data: { orderArchiveIDs },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createArchiveOrders(orderArchiveIDs);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const getOrderDispatchDetails = async (orderID, deliveryType) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/order_dispatch/fetch`,
      data: { orderID: orderID, deliveryType: deliveryType },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await getOrderDispatchDetails(orderID, deliveryType);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

//AIRWAY BILL

export const getAirwayBills = async (orderIDs) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/download_airway_bill/update`,
      data: { orderIDs },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await getAirwayBills(orderIDs);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// Set handover point for bulk and loose

export const updateOrdersHandoverPoint = async (payload) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/assign_handoverPoint/update`,
      data: {
        orderIDs: payload.orderIDs,
        handoverPointID: payload.handoverPointID,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateOrdersHandoverPoint(payload);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// set driver and handover point

export const updateDriverAndHandoverPoint = async (payload) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/order/assign_driver/update`,
      data: { ...payload },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateDriverAndHandoverPoint(payload);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

/**
 * Update delivery date
 */
export const updateOrderDeliveryDate = async (data) => {
  const accessToken = getAccessToken();

  let startOfDeliveryDate = data?.deliveryDate;

  if (startOfDeliveryDate) {
    startOfDeliveryDate = moment(startOfDeliveryDate).startOf("day");
  }

  try {
    const response: any = await axios({
      method: "POST",
      url: `${url}/order/delivery_date/update`,
      data: omitBy(
        {
          orderID: data.orderID,
          deliveryDate: startOfDeliveryDate,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error ? response.data : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateOrderDeliveryDate(data);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updatePickupDateTime = async (payload: {
  pickupDateTime: Moment;
  orderID: string;
}) => {
  const accessToken = getAccessToken();
  try {
    const response: any = await axios({
      method: "POST",
      url: apiURL("/pickup/pickup_date/update"),
      data: omitBy(
        {
          orderID: payload.orderID,
          pickupDate: payload.pickupDateTime,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error ? response.data : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updatePickupDateTime(payload);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
